import { loadSession, clearSession } from './tokenStorage';

export class SessionExpiredError extends Error {
  constructor(message = 'Session expired. Please sign in again.') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

let onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

export async function parseApiError(res: Response): Promise<string> {
  try {
    const data = await res.json();
    return (data as { error?: string }).error || res.statusText;
  } catch {
    return res.statusText;
  }
}

export function buildAuthHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const session = loadSession();
  const headers: Record<string, string> = { ...extra };
  if (session?.token) {
    headers['Authorization'] = `Bearer ${session.token}`;
  }
  return headers;
}

function requestHadAuth(init?: RequestInit): boolean {
  const headers = init?.headers;
  if (!headers) return false;
  if (headers instanceof Headers) {
    return headers.has('Authorization');
  }
  if (Array.isArray(headers)) {
    return headers.some(([k]) => k.toLowerCase() === 'authorization');
  }
  return Object.keys(headers as Record<string, string>).some(
    (k) => k.toLowerCase() === 'authorization'
  );
}

/** Authenticated API fetch — 401 clears session and redirects to login via AuthContext. */
export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const res = await fetch(input, init);
  if (res.status === 401 && requestHadAuth(init)) {
    clearSession();
    onSessionExpired?.();
    throw new SessionExpiredError(await parseApiError(res));
  }
  return res;
}
